// components/TaskCard.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import toast from "react-hot-toast";
import "./TaskCard.css"
// Enhanced RootPortal component
const RootPortal = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Create a portal container if needed
    let portalRoot = document.getElementById("portal-root");
    if (!portalRoot) {
      portalRoot = document.createElement("div");
      portalRoot.id = "portal-root";
      portalRoot.style.position = "relative";
      portalRoot.style.zIndex = "9999";
      document.body.appendChild(portalRoot);
    }

    return () => {
      setMounted(false);
      // Optional: clean up portal root if empty
      const portal = document.getElementById("portal-root");
      if (portal && !portal.hasChildNodes()) {
        document.body.removeChild(portal);
      }
    };
  }, []);

  // Use the portal root if it exists, otherwise fallback to document.body
  const getPortalContainer = () => {
    return document.getElementById("portal-root") || document.body;
  };

  return mounted ? ReactDOM.createPortal(children, getPortalContainer()) : null;
};

// Task Status Mapping
const TASK_STATUS_MAP = {
  1: {
    id: 1,
    name: "To Do",
    icon: "fa-circle",
    color: "#6c757d",
    bgColor: "#e9ecef",
  },
  2: {
    id: 2,
    name: "In Progress",
    icon: "fa-spinner",
    color: "#fd7e14",
    bgColor: "#fff3e0",
  },
  3: {
    id: 3,
    name: "Archived",
    icon: "fa-archive",
    color: "#6c757d",
    bgColor: "#f8d7da",
  },
  4: {
    id: 4,
    name: "Blocked",
    icon: "fa-times",
    color: "#dc3545",
    bgColor: "#f8d7da",
  },
  5: {
    id: 5,
    name: "Done",
    icon: "fa-check-circle",
    color: "#28a745",
    bgColor: "#d4edda",
  },
};

const TaskCard = ({
  task,
  isExpanded,
  onToggle,
  onNotesToggle,
  hasNotes,
  children,
  // Task-specific props
  onTaskDueDateSelect,
  onTaskAssigneeSelect,
  onTaskStatusSelect,
  onAddNote,
  onEdit,
  onDelete,
  userId,
  refreshNotes,
  job,
  fetchTasksForJob,
  isHighlighted = false
}) => {
  const taskRef = useRef(null);
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);

  // Task popup states
  const [showTaskDueDatePopup, setShowTaskDueDatePopup] = useState(false);
  const [showTaskAssigneePopup, setShowTaskAssigneePopup] = useState(false);
  const [showTaskStatusPopup, setShowTaskStatusPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Calendar mode state
  const [calendarMode, setCalendarMode] = useState("dueDate"); // 'dueDate' or 'duration'

  // Initialize task data
  const [selectedDueDate, setSelectedDueDate] = useState(task.dueDate || null);
  const [selectedStartDate, setSelectedStartDate] = useState(
    task.startDate || null,
  );
  const [selectedEndDate, setSelectedEndDate] = useState(task.endDate || null);
  const [deleteError, setDeleteError] = useState(null);

  // Multiple assignees state
  const [selectedAssignees, setSelectedAssignees] = useState(() => {
    // Check if there are assigned users
    if (task.assignedUsers && task.assignedUsers.length > 0) {
      return task.assignedUsers.map((user) => ({
        id: user.userId,
        name: user.fullName || user.username,
        username: user.username,
      }));
    }
    // Fallback to old properties if they exist
    return task.assigneeId
      ? [{ id: task.assigneeId, name: task.assigneeName }]
      : [];
  });

  const [selectedStatus, setSelectedStatus] = useState(
    TASK_STATUS_MAP[task.status] || TASK_STATUS_MAP[1],
  );

  // Temporary selection states for duration mode
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);

  // Users state
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsersList, setLoadingUsersList] = useState(false);
  const [usersError, setUsersError] = useState(null);

  // Loading states for updates
  const [updatingTask, setUpdatingTask] = useState(false);
  const [updatingAssignees, setUpdatingAssignees] = useState(false);
  const [deletingTask, setDeletingTask] = useState(false);

  // Popup position state
  const [popupPosition, setPopupPosition] = useState({
    dueDate: { top: 0, left: 0 },
    assignee: { top: 0, left: 0 },
    status: { top: 0, left: 0 },
    deleteConfirm: { top: 0, left: 0 },
  });

  // Refs for the icons
  const calendarIconRef = useRef(null);
  const assigneeIconRef = useRef(null);
  const statusIconRef = useRef(null);

  

  // API base URL
  const apiUrl = process.env.REACT_APP_API_BASE_URL || "";

  const getStatusBadge = (status) => {
    const statusMap = {
      1: { label: "To Do", color: "#c2c2c2" },
      2: { label: "In Progress", color: "#ff8400" },
      3: { label: "Archived", color: "#c2c2c2" },
      4: { label: "Blocked", color: "#dc3545" },
      5: { label: "Done", color: "#28a745" },
    };
    return statusMap[status] || { label: "Unknown", color: "#6c757d" };
  };

  const status = getStatusBadge(task.status);
  const dueDate = task.dueDate ? new Date(task.dueDate) : null;
  const isOverdue =
    dueDate &&
    dueDate < new Date() &&
    task.status !== 2 &&
    selectedStatus?.id !== 4;

  const handleTaskToggle = (e) => {
    e.stopPropagation();
    onToggle(task.id);
  };

  const handleNotesToggle = (e) => {
    e.stopPropagation();
    const newState = !isNotesExpanded;
    setIsNotesExpanded(newState);
    if (onNotesToggle) {
      onNotesToggle(task.id, newState);
    }
  };

  // Function to update task with toast notifications
  const updateTask = useCallback(
    async (updates) => {
      if (!task.id) {
        console.error("No taskId found for task:", task);
        toast.error("Cannot update task: Task ID not found");
        return;
      }

      setUpdatingTask(true);

      // Determine what's being updated for the toast message
      let updateType = "";
      if (updates.dueDate !== undefined) updateType = "Due date";
      else if (updates.startDate !== undefined || updates.endDate !== undefined)
        updateType = "Duration";
      else if (updates.status !== undefined) updateType = "Status";

      try {
        // Build the complete task payload
        const payload = {
          title: task.title || "",
          description: task.description || "",
          startDate:
            updates.startDate !== undefined
              ? updates.startDate
              : task.startDate || new Date().toISOString(),
          endDate:
            updates.endDate !== undefined
              ? updates.endDate
              : task.endDate || null,
          dueDate:
            updates.dueDate !== undefined
              ? updates.dueDate
              : selectedDueDate || null,
          jobId: task.jobId || 0,
          status:
            updates.status !== undefined
              ? updates.status
              : selectedStatus?.id || 1,
          ...updates,
        };

        console.log("Updating task with payload:", payload);

        const response = await fetch(`${apiUrl}/api/JobTasks/${task.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Failed to update task: ${response.status} - ${errorText}`,
          );
        }

        const result = await response.json();
        console.log("Task updated successfully:", result);

        // Show success toast
        if (updateType) {
          toast.success(`${updateType} updated successfully`);
        } else {
          toast.success("Task updated successfully");
        }

        // Call the parent's callback if provided
        if (updates.dueDate !== undefined && onTaskDueDateSelect) {
          onTaskDueDateSelect(task, updates.dueDate);
        }
        if (
          (updates.startDate !== undefined || updates.endDate !== undefined) &&
          onTaskDueDateSelect
        ) {
          onTaskDueDateSelect(task, {
            startDate: updates.startDate,
            endDate: updates.endDate,
          });
        }
        if (updates.status !== undefined && onTaskStatusSelect) {
          onTaskStatusSelect(task, { id: updates.status });
        }
        fetchTasksForJob(job.jobId);
        // Trigger refresh if available
        if (refreshNotes) {
          refreshNotes();
        }

        return result;
      } catch (error) {
        console.error("Error updating task:", error);

        let errorMessage = `Failed to update ${updateType.toLowerCase() || "task"}`;

        if (error.message.includes("Failed to fetch")) {
          errorMessage = "Network error. Please check your connection.";
        } else if (error.message.includes("500")) {
          errorMessage = "Server error. Please try again later.";
        } else {
          errorMessage = error.message;
        }

        toast.error(errorMessage);
      } finally {
        setUpdatingTask(false);
      }
    },
    [
      task,
      selectedDueDate,
      selectedStatus,
      userId,
      apiUrl,
      onTaskDueDateSelect,
      onTaskStatusSelect,
      refreshNotes,
    ],
  );

    useEffect(() => {
    if (isHighlighted && taskRef.current) {
      taskRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  }, [isHighlighted])

  useEffect(() => {
  if (isHighlighted) {
    console.log('Task is highlighted:', task.id, task.title);
    console.log('Task ref:', taskRef.current);
    console.log('Has task-highlighted class:', taskRef.current?.classList.contains('task-highlighted'));
  }
}, [isHighlighted, task.id, task.title]);

  // Function to add assignee to task
  const addAssignee = useCallback(
    async (user) => {
      if (!task.id || !userId) {
        toast.error("Cannot add assignee: Missing task ID or user ID");
        return;
      }

      setUpdatingAssignees(true);

      try {
        const payload = {
          userId: user.id,
          jobTaskId: task.id,
          assignedById: parseInt(userId),
        };

        console.log("Adding assignee with payload:", payload);

        const response = await fetch(`${apiUrl}/api/UserJobTaskAssignments`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorText = await response.text();

          // Check if it's a duplicate assignment error
          if (
            response.status === 409 ||
            errorText.includes("already assigned")
          ) {
            toast.error(`${user.name} is already assigned to this task`);
            return;
          }

          throw new Error(
            `Failed to add assignee: ${response.status} - ${errorText}`,
          );
        }

        // Add user to selected assignees list
        setSelectedAssignees((prev) => [
          ...prev,
          { id: user.id, name: user.name },
        ]);

        toast.success(`${user.name} assigned successfully`);

        // Call parent callback if provided
        if (onTaskAssigneeSelect) {
          onTaskAssigneeSelect(task, {
            assignees: [...selectedAssignees, user],
          });
        }
        fetchTasksForJob(job.jobId);
        // Trigger refresh if available
        if (refreshNotes) {
          refreshNotes();
        }
      } catch (error) {
        console.error("Error adding assignee:", error);

        let errorMessage = `Failed to assign ${user.name}`;

        if (error.message.includes("Failed to fetch")) {
          errorMessage = "Network error. Please check your connection.";
        } else if (error.message.includes("500")) {
          errorMessage = "Server error. Please try again later.";
        } else {
          errorMessage = error.message;
        }

        toast.error(errorMessage);
      } finally {
        setUpdatingAssignees(false);
      }
    },
    [
      task.id,
      userId,
      apiUrl,
      onTaskAssigneeSelect,
      refreshNotes,
      selectedAssignees,
    ],
  );

  // Function to remove assignee from task
  const removeAssignee = useCallback(
    async (user) => {
      if (!task.id) {
        toast.error("Cannot remove assignee: Missing task ID");
        return;
      }

      setUpdatingAssignees(true);

      try {
        // Using the DELETE endpoint: api/UserJobTaskAssignments/task/{taskId}/user/{userId}
        const response = await fetch(
          `${apiUrl}/api/UserJobTaskAssignments/task/${task.id}/user/${user.id}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok && response.status !== 404) {
          const errorText = await response.text();
          throw new Error(
            `Failed to remove assignee: ${response.status} - ${errorText}`,
          );
        }

        // Remove user from selected assignees list
        setSelectedAssignees((prev) => prev.filter((a) => a.id !== user.id));

        toast.success(`${user.name} removed successfully`);

        // Call parent callback if provided
        if (onTaskAssigneeSelect) {
          onTaskAssigneeSelect(task, {
            assignees: selectedAssignees.filter((a) => a.id !== user.id),
          });
        }
        fetchTasksForJob(job.jobId);
        // Trigger refresh if available
        if (refreshNotes) {
          refreshNotes();
        }
      } catch (error) {
        console.error("Error removing assignee:", error);

        let errorMessage = `Failed to remove ${user.name}`;

        if (error.message.includes("Failed to fetch")) {
          errorMessage = "Network error. Please check your connection.";
        } else if (error.message.includes("500")) {
          errorMessage = "Server error. Please try again later.";
        } else {
          errorMessage = error.message;
        }

        toast.error(errorMessage);
      } finally {
        setUpdatingAssignees(false);
      }
    },
    [task.id, apiUrl, onTaskAssigneeSelect, refreshNotes],
  );

  // Function to delete task
const confirmDelete = useCallback(async () => {
  if (!task.id) {
    setDeleteError("Cannot delete task: Task ID not found");
    return;
  }

  setDeletingTask(true);
  setDeleteError(null); 

  try {
    const response = await fetch(`${apiUrl}/api/JobTasks/${task.id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMsg = "";
      
      if (response.status === 500) {
        errorMsg = "Unable to delete task. This task may contain notes or be referenced elsewhere.";
      } else if (response.status === 404) {
        errorMsg = "Task not found. It may have already been deleted.";
      } else if (response.status === 409) {
        errorMsg = "Cannot delete task. It is being used by other records.";
      } else {
        errorMsg = `Failed to delete task: ${response.status}`;
      }
      
      throw new Error(errorMsg);
    }

    setShowDeleteConfirm(false);
    setDeleteError(null);
    toast.success("Task deleted successfully");

    if (onDelete) {
      onDelete(task.id);
    }
    fetchTasksForJob(job.jobId);
    if (refreshNotes) {
      refreshNotes();
    }
  } catch (error) {
    console.error("Error deleting task:", error);
    setDeleteError(error.message);
  } finally {
    setDeletingTask(false);
  }
}, [task.id, apiUrl, onDelete, refreshNotes, job, fetchTasksForJob]);

  // Function to fetch users
const fetchUsers = useCallback(async () => {
  if (!userId) {
    console.log("No userId provided");
    setUsersError("User ID not available");
    setLoadingUsersList(false);
    return;
  }

  setLoadingUsersList(true);
  setUsersError(null);

  try {
    const url = `${apiUrl}/api/Permissions/permissions-by-job/${job.jobId}`;
    console.log("Fetching users from:", url);

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch users: ${response.status}`);
    }

    const data = await response.json();
    console.log("Users data received:", data);

    if (data && data.permissions && Array.isArray(data.permissions)) {
      // Check for duplicate IDs and ensure uniqueness
      const uniqueUsersMap = new Map();
      
      data.permissions.forEach((user) => {
        if (!uniqueUsersMap.has(user.userId)) {
          uniqueUsersMap.set(user.userId, {
            id: user.userId,
            name: user.userName,
            color: getRandomColor(user.userId),
          });
        }
      });
      
      const transformedUsers = Array.from(uniqueUsersMap.values());
      console.log("Transformed users:", transformedUsers);
      setAvailableUsers(transformedUsers);
    } else {
      console.log("No usernames array in response");
      setAvailableUsers([]);
      toast.error("No users found");
    }
  } catch (error) {
    console.error("Error fetching users:", error);
    setUsersError(error.message);
    setAvailableUsers([]);
    toast.error("Failed to load users list");
  } finally {
    setLoadingUsersList(false);
  }
}, [userId, apiUrl, job]);

  // Fetch users when assignee popup is opened
  useEffect(() => {
    if (showTaskAssigneePopup) {
      console.log("Assignee popup opened, fetching users...");
      fetchUsers();
    }
  }, [showTaskAssigneePopup, fetchUsers]);

  // Function to calculate popup position
  const calculatePopupPosition = useCallback(
    (elementRef, popupType = "calendar") => {
      if (!elementRef.current) return { top: 0, left: 0 };

      const rect = elementRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Different popup heights based on type
      let popupHeight = 400; // default for calendar
      let popupWidth = 280; // default width

      if (popupType === "assignee") {
        popupHeight = 350;
        popupWidth = 240;
      } else if (popupType === "status") {
        popupHeight = 240; // Status popup is smaller
        popupWidth = 200;
      }

      let top = rect.bottom + 5;
      let left = rect.left;

      // Check if popup would go below viewport
      if (top + popupHeight > viewportHeight) {
        top = rect.top - popupHeight + 80;
      }

      // Check if popup would go beyond right edge
      if (left + popupWidth > viewportWidth) {
        left = viewportWidth - popupWidth - 10;
      }

      // Ensure popup doesn't go above viewport
      if (top < 0) top = 10;
      // Ensure popup doesn't go left of viewport
      if (left < 0) left = 10;

      return { top, left };
    },
    [],
  );

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showTaskDueDatePopup ||
        showTaskAssigneePopup ||
        showTaskStatusPopup ||
        showDeleteConfirm
      ) {
        const isClickOnCalendar = calendarIconRef.current?.contains(e.target);
        const isClickOnAssignee = assigneeIconRef.current?.contains(e.target);
        const isClickOnStatus = statusIconRef.current?.contains(e.target);

        const isClickInsideCalendar = e.target.closest?.(
          ".calendar-popup-root",
        );
        const isClickInsideAssignee = e.target.closest?.(".user-popup-root");
        const isClickInsideStatus = e.target.closest?.(".status-popup-root");
        const isClickInsideDelete = e.target.closest?.(".delete-confirm-modal");

        if (!isClickOnCalendar && !isClickInsideCalendar) {
          setShowTaskDueDatePopup(false);
          // Reset temp dates when closing
          setTempStartDate(null);
          setTempEndDate(null);
          setCalendarMode("dueDate");
        }
        if (!isClickOnAssignee && !isClickInsideAssignee) {
          setShowTaskAssigneePopup(false);
        }
        if (!isClickOnStatus && !isClickInsideStatus) {
          setShowTaskStatusPopup(false);
        }
        // Don't auto-close delete confirmation
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [
    showTaskDueDatePopup,
    showTaskAssigneePopup,
    showTaskStatusPopup,
    showDeleteConfirm,
  ]);

  // Update position on scroll/resize
  useEffect(() => {
    const updatePositions = () => {
      if (showTaskDueDatePopup) {
        setPopupPosition((prev) => ({
          ...prev,
          dueDate: calculatePopupPosition(calendarIconRef, "calendar"),
        }));
      }
      if (showTaskAssigneePopup) {
        setPopupPosition((prev) => ({
          ...prev,
          assignee: calculatePopupPosition(assigneeIconRef, "assignee"),
        }));
      }
      if (showTaskStatusPopup) {
        setPopupPosition((prev) => ({
          ...prev,
          status: calculatePopupPosition(statusIconRef, "status"),
        }));
      }
    };

    if (showTaskDueDatePopup || showTaskAssigneePopup || showTaskStatusPopup) {
      window.addEventListener("scroll", updatePositions, true);
      window.addEventListener("resize", updatePositions);
    }

    return () => {
      window.removeEventListener("scroll", updatePositions, true);
      window.removeEventListener("resize", updatePositions);
    };
  }, [
    showTaskDueDatePopup,
    showTaskAssigneePopup,
    showTaskStatusPopup,
    calculatePopupPosition,
  ]);

  // Calendar functions
  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  const handleDateSelect = (date) => {
    if (calendarMode === "dueDate") {
      // For due date mode, directly update
      handleDueDateSave(date);
    } else {
      // For duration mode, manage temp selections
      if (!tempStartDate) {
        setTempStartDate(date);
        setTempEndDate(null);
      } else if (!tempEndDate && date >= tempStartDate) {
        setTempEndDate(date);
      } else {
        // Reset and start new selection
        setTempStartDate(date);
        setTempEndDate(null);
      }
    }
  };

  const handleDueDateSave = async (date) => {
    setSelectedDueDate(date);
    setShowTaskDueDatePopup(false);

    const toastId = toast.loading(
      date ? "Updating due date..." : "Clearing due date...",
    );
    await updateTask({ dueDate: date });
    toast.dismiss(toastId);
  };

  const handleDurationSave = async () => {
    if (tempStartDate && tempEndDate) {
      setSelectedStartDate(tempStartDate);
      setSelectedEndDate(tempEndDate);
      setShowTaskDueDatePopup(false);

      const toastId = toast.loading("Updating duration...");
      await updateTask({
        startDate: tempStartDate.toISOString(),
        endDate: tempEndDate.toISOString(),
        dueDate: null, // Clear due date when setting duration
      });
      toast.dismiss(toastId);

      // Reset temp dates
      setTempStartDate(null);
      setTempEndDate(null);
    } else {
      toast.error("Please select both start and end dates");
    }
  };

  const handleAssigneeSelect = async (user) => {
    // Don't allow assignee changes if task is archived
    if (selectedStatus?.id === 3) {
      toast.error("Cannot modify assignees for archived tasks");
      return;
    }

    // Check if user is already assigned
    const isAlreadyAssigned = selectedAssignees.some((a) => a.id === user.id);

    if (isAlreadyAssigned) {
      // Remove assignee
      await removeAssignee(user);
    } else {
      // Add assignee
      await addAssignee(user);
    }

    setShowTaskAssigneePopup(false);
  };

  const handleStatusSelect = async (status) => {
    setSelectedStatus(status);
    setShowTaskStatusPopup(false);

    const toastId = toast.loading(`Changing status to ${status.name}...`);
    await updateTask({ status: status.id });
    toast.dismiss(toastId);
  };

  const toggleCalendarMode = (mode) => {
    setCalendarMode(mode);
    setTempStartDate(null);
    setTempEndDate(null);
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Add days from previous month
    for (let i = startingDay - 1; i >= 0; i--) {
      const prevMonthDate = new Date(year, month - 1, daysInPrevMonth - i);
      prevMonthDate.setHours(0, 0, 0, 0);

      const isSelected =
        calendarMode === "dueDate"
          ? selectedDueDate &&
            new Date(selectedDueDate).toDateString() ===
              prevMonthDate.toDateString()
          : (tempStartDate &&
              tempStartDate.toDateString() === prevMonthDate.toDateString()) ||
            (tempEndDate &&
              tempEndDate.toDateString() === prevMonthDate.toDateString());

      const isInRange =
        tempStartDate &&
        tempEndDate &&
        prevMonthDate >= tempStartDate &&
        prevMonthDate <= tempEndDate;

      days.push(
        <div
          key={`prev-${i}`}
          className={`calendar-day other-month ${isSelected ? "selected" : ""} ${isInRange ? "in-range" : ""}`}
          onClick={() => handleDateSelect(prevMonthDate)}
          style={{
            backgroundColor: isInRange ? "#e3f2fd" : "transparent",
          }}
        >
          {daysInPrevMonth - i}
        </div>,
      );
    }

    // Add days of the current month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      date.setHours(0, 0, 0, 0);

      const isSelected =
        calendarMode === "dueDate"
          ? selectedDueDate &&
            new Date(selectedDueDate).toDateString() === date.toDateString()
          : (tempStartDate &&
              tempStartDate.toDateString() === date.toDateString()) ||
            (tempEndDate && tempEndDate.toDateString() === date.toDateString());

      const isInRange =
        tempStartDate &&
        tempEndDate &&
        date >= tempStartDate &&
        date <= tempEndDate;

      const isToday = date.toDateString() === today.toDateString();
      const isStart =
        tempStartDate && tempStartDate.toDateString() === date.toDateString();
      const isEnd =
        tempEndDate && tempEndDate.toDateString() === date.toDateString();

      days.push(
        <div
          key={d}
          className={`calendar-day current-month ${isSelected ? "selected" : ""} ${isToday ? "today" : ""} ${isStart ? "range-start" : ""} ${isEnd ? "range-end" : ""}`}
          onClick={() => handleDateSelect(date)}
          style={{
            backgroundColor: isInRange ? "#e3f2fd" : "transparent",
            border: isStart || isEnd ? "2px solid #1976d2" : "none",
          }}
        >
          {d}
        </div>,
      );
    }

    return days;
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getRandomColor = (id) => {
    const colors = [
      "#1976d2",
      "#dc3545",
      "#28a745",
      "#fd7e14",
      "#6f42c1",
      "#20c997",
      "#e83e8c",
      "#007bff",
    ];
    return colors[(id || 0) % colors.length];
  };

  const handleDueDateClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Don't allow date changes if task is archived
    if (selectedStatus?.id === 3) {
      toast.error("Cannot modify dates for archived tasks");
      return;
    }

    setShowTaskDueDatePopup(!showTaskDueDatePopup);
    setShowTaskAssigneePopup(false);
    setShowTaskStatusPopup(false);
    setShowDeleteConfirm(false);
    setCalendarMode("dueDate");
    setTempStartDate(null);
    setTempEndDate(null);
    setPopupPosition((prev) => ({
      ...prev,
      dueDate: calculatePopupPosition(calendarIconRef, "calendar"),
    }));
  };

  const handleAssigneeClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Don't allow assignee changes if task is archived
    if (selectedStatus?.id === 3) {
      toast.error("Cannot modify assignees for archived tasks");
      return;
    }

    setShowTaskAssigneePopup(!showTaskAssigneePopup);
    setShowTaskDueDatePopup(false);
    setShowTaskStatusPopup(false);
    setShowDeleteConfirm(false);
    setPopupPosition((prev) => ({
      ...prev,
      assignee: calculatePopupPosition(assigneeIconRef, "assignee"),
    }));
  };

  const handleStatusClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Status can be changed even for archived tasks
    setShowTaskStatusPopup(!showTaskStatusPopup);
    setShowTaskDueDatePopup(false);
    setShowTaskAssigneePopup(false);
    setShowDeleteConfirm(false);
    setPopupPosition((prev) => ({
      ...prev,
      status: calculatePopupPosition(statusIconRef, "status"),
    }));
  };

  // Function to render status icon based on status ID
  const renderStatusIcon = () => {
    const statusId = selectedStatus?.id;
    const color = selectedStatus?.color || "#6c757d";

    switch (statusId) {
      case 1:
        return (
          <i
            className="fas fa-check"
            style={{ fontSize: "14px", color: color }}
          ></i>
        );
      case 2:
        return (
          <i
            className="fas fa-check-double"
            style={{ fontSize: "14px", color: color }}
          ></i>
        );
      case 3:
        return (
          <i
            className="fas fa-archive"
            style={{ fontSize: "16px", color: color }}
          ></i>
        );
      case 4:
        return (
          <i
            className="fas fa-times"
            style={{ fontSize: "18px", color: color }}
          ></i>
        );
      case 5:
        return (
          <i
            className="fas fa-check-circle"
            style={{ fontSize: "18px", color: color }}
          ></i>
        );
      default:
        return (
          <i
            className="fas fa-check"
            style={{ fontSize: "14px", color: "#6c757d" }}
          ></i>
        );
    }
  };

  const handleAddNoteClick = (e) => {
    console.log(task);
    e.stopPropagation();

    // Don't allow adding notes if task is archived
    if (selectedStatus?.id === 3) {
      toast.error("Cannot add notes to archived tasks");
      return;
    }
    if (onAddNote) {
      onAddNote(task);
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();

    // Don't allow editing if task is archived
    if (selectedStatus?.id === 3) {
      toast.error("Cannot edit archived tasks");
      return;
    }

    if (onEdit) {
      onEdit(task);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDeleteConfirm(true);
    setShowTaskDueDatePopup(false);
    setShowTaskAssigneePopup(false);
    setShowTaskStatusPopup(false);
  };

  // Function to render assignee avatars
  const renderAssigneeAvatars = () => {
    if (selectedAssignees.length === 0) {
      return <span style={{ color: "#999" }}>Unassigned</span>;
    }

    return (
      <div
        style={{
          display: "flex",
          fontWeight: "500",
          alignItems: "center",
          gap: "4px",
          flexWrap: "wrap",
        }}
      >
        {selectedAssignees.map((assignee, index) => (
          <div
            key={assignee.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "2px 6px",
              backgroundColor: getRandomColor(assignee.id) + "20",
              borderRadius: "12px",
              fontSize: "11px",
              maxWidth: "100px",
              minWidth: "0",
              flexShrink: 1,
            }}
            title={assignee.name}
          >
            <span
              style={{
                maxWidth: "80px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
              }}
            >
              {assignee.name}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const getNotesCounterColor = () => {
    switch (selectedStatus?.id) {
      case 1:
        return "#6c757d";
      case 2:
        return "#fd7e14";
      case 3:
        return "#6c757d";
      case 4:
        return "#dc3545";
      case 5:
        return "#28a745";
      default:
        return "#fd7e14";
    }
  };

  // Get footer background color based on status
  const getFooterBackgroundColor = () => {
    switch (selectedStatus?.id) {
      case 1: // To Do
        return "#bcb4b4";
      case 2: // In Progress
        return "#fcc46b";
      case 3: // Archived
        return "#bcb4b4";
      case 4: // Blocked
        return "#f88c8c";
      case 5: // Done
        return "#76f080";
      default:
        return "#f8f9fa";
    }
  };

  const calculateDaysData = () => {
    // If task status is "Archived" (status 3), don't show days data
    if (selectedStatus?.id === 3) {
      return null;
    }

    // If task status is "Done" (status 5), show completed state
    if (selectedStatus?.id === 5) {
      return {
        completed: 100,
        remaining: 0,
        total: 100,
        isCompleted: true,
        completedAt: task.actualEndDate
          ? new Date(task.actualEndDate)
          : new Date(),
      };
    }

    if (!task.startDate || !task.endDate) return null;

    const startDate = new Date(task.startDate);
    const endDate = new Date(task.endDate);
    const currentDate = new Date();

    // Calculate total duration in days (endDate - startDate)
    const totalDurationMs = endDate - startDate;
    const totalDays = Math.ceil(totalDurationMs / (1000 * 60 * 60 * 24));

    // Calculate remaining days (endDate - currentDate)
    const remainingMs = endDate - currentDate;
    const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));

    // Calculate completed days (totalDays - remainingDays)
    let completedDays = totalDays - remainingDays;

    // Handle edge cases
    if (remainingDays < 0) {
      // Task is overdue
      return {
        completed: totalDays,
        remaining: 0,
        total: totalDays,
        isOverdue: true,
        overdueBy: Math.abs(remainingDays),
      };
    }

    if (currentDate < startDate) {
      // Task hasn't started yet
      return {
        completed: 0,
        remaining: totalDays,
        total: totalDays,
        notStarted: true,
      };
    }

    return {
      completed: Math.min(completedDays, totalDays),
      remaining: Math.max(remainingDays, 0),
      total: totalDays,
      isActive: true,
    };
  };

  const daysData = calculateDaysData();
  const isCompleted = selectedStatus?.id === 5;
  const isArchived = selectedStatus?.id === 3;

  // Get status color for note count badge
  const getStatusColor = () => {
    if (isCompleted) return "#28a745";
    if (isArchived) return "#6c757d";
    if (selectedStatus?.id === 2) return "#fd7e14";
    if (selectedStatus?.id === 4) return "#dc3545";
    return "#6c757d"; // Default for To Do
  };

  return (
    <>
      <div
        ref={taskRef}
        className={`task-card ${isHighlighted ? 'task-highlighted' : ''}`}
        onClick={handleTaskToggle}
        style={{
          transition: 'all 0.3s ease',
          ...(isHighlighted && {
            backgroundColor: '#fff3cd',
            border: '2px solid #ffc107',
            boxShadow: '0 0 0 3px rgba(255, 193, 7, 0.3)',
            transform: 'scale(1.02)'
          }),
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "16px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          border: isArchived
            ? "1px solid #e0e0e0"
            : isCompleted
              ? "1px solid #d4edda"
              : "1px solid #e0e0e0",
          transition: "all 0.3s ease",
          cursor: "pointer",
          position: "relative",
          height: "260px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          opacity: isArchived ? 0.85 : 1,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
        }}
      >
        
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "12px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className="fas fa-tasks" style={{ color: getStatusColor() }} />
            <span
              style={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#2c3e50",
              }}
            >
              {task.friendlyId || `Task #${task.id}`}
            </span>
          </div>

          <div
            style={{
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "11px",
              fontWeight: 600,
              backgroundColor: getNotesCounterColor(),
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              transition: "background-color 0.3s ease",
            }}
          >
            <i className="fas fa-tasks" style={{ fontSize: "10px" }} />
            {task.siteNoteCount} Notes
          </div>
        </div>

        {/* Title */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            marginBottom: "8px",
            flexShrink: 0,
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: 600,
              color: isArchived ? "#6c757d" : isCompleted ? "#6c757d" : "#333",
              lineHeight: "1.4",
              flex: "1 1 auto",
              textDecoration: isArchived
                ? "none"
                : isCompleted
                  ? "line-through"
                  : "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {task.title}
          </h4>

          {/* Hide days left card if archived */}
          {daysData && !isArchived && (
            <div
              style={{
                backgroundColor: isCompleted
                  ? "#d4edda"
                  : daysData.isOverdue
                    ? "#fff5f5"
                    : daysData.notStarted
                      ? "#e8f4fd"
                      : "#f8f9fa",
                borderRadius: "8px 4px 8px 4px",
                padding: "4px 10px",
                boxShadow: isCompleted
                  ? "0 3px 6px rgba(40, 167, 69, 0.2)"
                  : "0 3px 6px rgba(0,0,0,0.1)",
                border: `1px solid ${
                  isCompleted
                    ? "#c3e6cb"
                    : daysData.isOverdue
                      ? "#ffebee"
                      : "#e9ecef"
                }`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: "70px",
                flexShrink: 0,
                marginLeft: "16px",
                transform: "skewX(-5deg)",
                position: "relative",
                overflow: "hidden",
                background: isCompleted
                  ? "linear-gradient(135deg, #d4edda 0%, #ffffff 100%)"
                  : daysData.isOverdue
                    ? "linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)"
                    : daysData.notStarted
                      ? "linear-gradient(135deg, #e8f4fd 0%, #ffffff 100%)"
                      : "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              }}
            >
              <div
                style={{
                  transform: "skewX(5deg)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {isCompleted ? (
                  // Completed state - show checkmark and completion date
                  <>
                    <i
                      className="fas fa-check-circle"
                      style={{
                        fontSize: "20px",
                        color: "#28a745",
                        marginBottom: "4px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#28a745",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Completed
                    </span>
                    {task.actualEndDate && (
                      <span
                        style={{
                          fontSize: "9px",
                          color: "#6c757d",
                          marginTop: "2px",
                        }}
                      >
                        {new Date(task.actualEndDate).toLocaleDateString()}
                      </span>
                    )}
                  </>
                ) : (
                  // Normal state - show progress
                  <>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: daysData.isOverdue ? "#dc3545" : "#fd7e14",
                        lineHeight: 1.2,
                      }}
                    >
                      {daysData.completed} / {daysData.total}
                    </span>
                    <span
                      style={{
                        fontSize: "10px",
                        color: daysData.isOverdue ? "#dc3545" : "#6c757d",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontWeight: 500,
                      }}
                    >
                      {daysData.isOverdue
                        ? `${daysData.overdueBy} days overdue`
                        : daysData.notStarted
                          ? "Not Started"
                          : "Days Left"}
                    </span>
                    {daysData.isActive &&
                      !daysData.isOverdue &&
                      !daysData.notStarted && (
                        <span
                          style={{
                            fontSize: "8px",
                            color: "#999",
                            marginTop: "2px",
                          }}
                        >
                          {daysData.remaining} remaining
                        </span>
                      )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Description (if exists) */}
        {task.description && (
          <p
            style={{
              margin: "0 0 12px 0",
              fontSize: "13px",
              color: isArchived ? "#999" : isCompleted ? "#999" : "#666",
              lineHeight: "1.5",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              textDecoration: isArchived
                ? "none"
                : isCompleted
                  ? "line-through"
                  : "none",
              wordBreak: "break-word",
              flexShrink: 0,
              maxHeight: "40px",
            }}
          >
            {task.description}
          </p>
        )}

        {/* Metadata - with truncation for long content */}
        <div
          style={{
            marginTop: "12px",
            paddingTop: "12px",
            borderTop: "1px solid #f0f0f0",
            flex: 1, // Allow metadata to take remaining space
            overflow: "hidden", // Hide overflow
          }}
        >
          {/* Due Date or Duration - only show if not completed or show as strikethrough */}
          {task.dueDate && !isCompleted && !isArchived && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
                fontSize: "12px",
                color: isOverdue ? "#dc3545" : "#666",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <i
                className="fas fa-calendar-alt"
                style={{ fontSize: "11px", flexShrink: 0 }}
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                Due: {dueDate.toLocaleDateString()}
                {isOverdue && " (Overdue)"}
              </span>
            </div>
          )}

          {/* Show completed due date with strikethrough if task is done */}
          {task.dueDate && isCompleted && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
                fontSize: "12px",
                color: "#6c757d",
              }}
            >
              <i
                className="fas fa-calendar-alt"
                style={{ fontSize: "11px", opacity: 0.6 }}
              />
              <span style={{ textDecoration: "line-through" }}>
                Due: {dueDate.toLocaleDateString()}
              </span>
            </div>
          )}

          {/* Show due date for archived tasks without interactivity */}
          {task.dueDate && isArchived && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
                fontSize: "12px",
                color: "#999",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <i
                className="fas fa-calendar-alt"
                style={{ fontSize: "11px", flexShrink: 0 }}
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                Due: {dueDate.toLocaleDateString()}
              </span>
            </div>
          )}

          {task.startDate && task.endDate && !isCompleted && !isArchived && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
                fontSize: "12px",
                color: "#666",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <i
                className="fas fa-clock"
                style={{ fontSize: "11px", flexShrink: 0 }}
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                Duration: {new Date(task.startDate).toLocaleDateString()} -{" "}
                {new Date(task.endDate).toLocaleDateString()}
              </span>
            </div>
          )}

          {/* Show completed duration with strikethrough if task is done */}
          {task.startDate && task.endDate && isCompleted && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
                fontSize: "12px",
                color: "#6c757d",
              }}
            >
              <i
                className="fas fa-clock"
                style={{ fontSize: "11px", opacity: 0.6 }}
              />
              <span style={{ textDecoration: "line-through" }}>
                Duration: {new Date(task.startDate).toLocaleDateString()} -{" "}
                {new Date(task.endDate).toLocaleDateString()}
              </span>
            </div>
          )}

          {/* Show duration for archived tasks without interactivity */}
          {task.startDate && task.endDate && isArchived && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
                fontSize: "12px",
                color: "#999",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <i
                className="fas fa-clock"
                style={{ fontSize: "11px", flexShrink: 0 }}
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                Duration: {new Date(task.startDate).toLocaleDateString()} -{" "}
                {new Date(task.endDate).toLocaleDateString()}
              </span>
            </div>
          )}

          {/* Multiple Assignees */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "6px",
              fontSize: "12px",
              color: isArchived ? "#999" : isCompleted ? "#999" : "#666",
            }}
          >
            <i
              className="fas fa-users"
              style={{
                fontSize: "11px",
                opacity: isArchived ? 0.6 : isCompleted ? 0.6 : 1,
              }}
            />
            <div style={{ flex: 1 }}>{renderAssigneeAvatars()}</div>
          </div>
        </div>

        

        {/* Actions Footer - Background color changes with status */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "12px",
            paddingTop: "8px",
            borderTop: "1px dashed #e9ecef",
            flexShrink: 0,
            padding: "8px 12px",
            borderRadius: "6px",
            backgroundColor: getFooterBackgroundColor(),
            transition: "background-color 0.3s ease",
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* Left side - Task icons (calendar, assignee, status) */}
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {/* Calendar/Due Date Icon - Disabled if archived */}
            <div
              ref={calendarIconRef}
              style={{ display: "inline-block", position: "relative" }}
            >
              <div
                className={`task-icon-item ${selectedDueDate || (selectedStartDate && selectedEndDate) ? "has-value" : ""} ${updatingTask ? "updating" : ""}`}
                title={
                  isArchived
                    ? "Cannot modify dates for archived tasks"
                    
                      : selectedDueDate
                        ? `Due: ${new Date(selectedDueDate).toLocaleDateString()}`
                        : selectedStartDate && selectedEndDate
                          ? `Duration: ${new Date(selectedStartDate).toLocaleDateString()} - ${new Date(selectedEndDate).toLocaleDateString()}`
                          : "Set Due Date or Duration"
                }
                onClick={isArchived ? null : handleDueDateClick}
                style={{
                  opacity: updatingTask || isArchived  ? 0.5 : 1,
                  cursor:
                    updatingTask || isArchived 
                      ? "not-allowed"
                      : "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  backgroundColor:
                    selectedDueDate || (selectedStartDate && selectedEndDate)
                      ? "#e3f2fd"
                      : "transparent",
                  transition: "all 0.2s ease",
                }}
              >
                <i
                  className="fas fa-calendar"
                  style={{
                    color:
                      selectedDueDate || (selectedStartDate && selectedEndDate)
                        ? "#1976d2"
                        : "#666",
                  }}
                ></i>
              </div>
            </div>

            {/* Assignee Icon - Disabled if archived */}
            <div
              ref={assigneeIconRef}
              style={{ display: "inline-block", position: "relative" }}
            >
              <div
                className={`task-icon-item ${selectedAssignees.length > 0 ? "has-value" : ""} ${updatingAssignees ? "updating" : ""}`}
                title={
                  isArchived
                    ? "Cannot modify assignees for archived tasks"
                      : `Assignees: ${selectedAssignees.length > 0 ? selectedAssignees.map((a) => a.name).join(", ") : "Unassigned"}`
                }
                onClick={isArchived ? null : handleAssigneeClick}
                style={{
                  opacity:
                    updatingAssignees || isArchived ? 0.5 : 1,
                  cursor:
                    updatingAssignees || isArchived
                      ? "not-allowed"
                      : "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  backgroundColor:
                    selectedAssignees.length > 0 ? "#e3f2fd" : "transparent",
                  transition: "all 0.2s ease",
                  position: "relative",
                }}
              >
                <i
                  className="fas fa-user"
                  style={{
                    color: selectedAssignees.length > 0 ? "#1976d2" : "#666",
                  }}
                ></i>
                {selectedAssignees.length > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-4px",
                      right: "-4px",
                      backgroundColor: "#fd7e14",
                      color: "white",
                      borderRadius: "50%",
                      width: "14px",
                      height: "14px",
                      fontSize: "9px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {selectedAssignees.length}
                  </span>
                )}
              </div>
            </div>

            {/* Status Icon - Always enabled */}
            <div
              ref={statusIconRef}
              style={{ display: "inline-block", position: "relative" }}
            >
              <div
                className={`task-icon-item ${selectedStatus ? "has-value" : ""} ${updatingTask ? "updating" : ""}`}
                title={`Status: ${selectedStatus?.name || "To Do"}`}
                onClick={handleStatusClick}
                style={{
                  opacity: updatingTask ? 0.5 : 1,
                  cursor: updatingTask ? "not-allowed" : "pointer",
                  padding: "4px",
                  minWidth: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  backgroundColor: selectedStatus
                    ? `${selectedStatus.bgColor}`
                    : "transparent",
                  transition: "all 0.2s ease",
                }}
              >
                {renderStatusIcon()}
              </div>
            </div>
          </div>

          {/* Right side - Action icons (add note, edit, delete) */}
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <button
              onClick={handleAddNoteClick}
              disabled={isArchived || isCompleted}
              style={{
                background: "transparent",
                border: "none",
                cursor: isArchived || isCompleted ? "not-allowed" : "pointer",
                color: isArchived || isCompleted ? "#ccc" : "#28a745",
                fontSize: "14px",
                padding: "4px 8px",
                borderRadius: "4px",
                transition: "all 0.2s ease",
                opacity: isArchived || isCompleted ? 0.5 : 1,
              }}
              title={
                isArchived
                  ? "Cannot add notes to archived tasks"
                  : isCompleted
                    ? "Cannot add notes to completed tasks"
                    : "Add Note"
              }
              className="action-btn"
              onMouseEnter={(e) => {
                if (!isArchived && !isCompleted) {
                  e.currentTarget.style.backgroundColor = "#e8f5e9";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <i className="fas fa-plus" />
            </button>

            <button
              onClick={handleEditClick}
              disabled={isArchived || isCompleted}
              style={{
                background: "transparent",
                border: "none",
                cursor: isArchived || isCompleted ? "not-allowed" : "pointer",
                color: isArchived || isCompleted ? "#ccc" : "#007bff",
                fontSize: "14px",
                padding: "4px 8px",
                borderRadius: "4px",
                transition: "all 0.2s ease",
                opacity: isArchived || isCompleted ? 0.5 : 1,
              }}
              title={
                isArchived
                  ? "Cannot edit archived tasks"
                  : isCompleted
                    ? "Cannot edit completed tasks"
                    : "Edit Task"
              }
              className="action-btn"
              onMouseEnter={(e) => {
                if (!isArchived && !isCompleted) {
                  e.currentTarget.style.backgroundColor = "#e3f2fd";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <i className="fas fa-edit" />
            </button>

            <button
              onClick={handleDeleteClick}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "#dc3545",
                fontSize: "14px",
                padding: "4px 8px",
                borderRadius: "4px",
                transition: "all 0.2s ease",
              }}
              title="Delete Task"
              className="action-btn"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#ffebee";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <i className="fas fa-trash" />
            </button>
          </div>
        </div>
      </div>

      {/* Render popups at root level using portal */}
      <RootPortal>
        {showTaskDueDatePopup && !isArchived  && (
          <CalendarPopup
            position={popupPosition.dueDate}
            currentMonth={currentMonth}
            goToPreviousMonth={goToPreviousMonth}
            goToNextMonth={goToNextMonth}
            generateCalendarDays={generateCalendarDays}
            monthNames={monthNames}
            selectedDueDate={selectedDueDate}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            tempStartDate={tempStartDate}
            tempEndDate={tempEndDate}
            calendarMode={calendarMode}
            toggleCalendarMode={toggleCalendarMode}
            handleDueDateSave={handleDueDateSave}
            handleDurationSave={handleDurationSave}
            updating={updatingTask}
          />
        )}

        {showTaskAssigneePopup && !isArchived && (
          <AssigneePopup
            position={popupPosition.assignee}
            users={availableUsers}
            loading={loadingUsersList}
            error={usersError}
            selectedAssignees={selectedAssignees}
            onTaskAssigneeSelect={handleAssigneeSelect}
            getInitials={getInitials}
            getRandomColor={getRandomColor}
            updating={updatingAssignees}
          />
        )}

        {showTaskStatusPopup && (
          <StatusPopup
            position={popupPosition.status}
            taskStatuses={[]}
            selectedStatus={selectedStatus}
            onTaskStatusSelect={handleStatusSelect}
            updating={updatingTask}
          />
        )}

        {showDeleteConfirm && (
          <DeleteConfirmationModal
            task={task}
            onConfirm={confirmDelete}
            onCancel={() => {
            setShowDeleteConfirm(false);
            setDeleteError(null); 
          }}
            deleting={deletingTask}
            errorMessage={deleteError}
          />
        )}
      </RootPortal>
    </>
  );
};

// Calendar Popup Component
const CalendarPopup = ({
  position,
  currentMonth,
  goToPreviousMonth,
  goToNextMonth,
  generateCalendarDays,
  monthNames,
  selectedDueDate,
  selectedStartDate,
  selectedEndDate,
  tempStartDate,
  tempEndDate,
  calendarMode,
  toggleCalendarMode,
  handleDueDateSave,
  handleDurationSave,
  updating,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);

  const handleSave = () => {
    if (calendarMode === "dueDate" && selectedDate) {
      handleDueDateSave(selectedDate);
    } else if (calendarMode === "duration") {
      handleDurationSave();
    }
  };

  return (
    <div
      className="calendar-popup-root"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 999999,
        backgroundColor: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
        width: "280px",
        padding: "12px",
        opacity: 1,
        color: "#333333",
        pointerEvents: updating ? "none" : "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {updating && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            zIndex: 1,
          }}
        >
          <i
            className="fas fa-spinner fa-spin"
            style={{ color: "#1976d2", fontSize: "24px" }}
          ></i>
        </div>
      )}

      {/* Mode Toggle */}
      <div
        style={{
          display: "flex",
          marginBottom: "12px",
          backgroundColor: "#f5f5f5",
          borderRadius: "6px",
          padding: "2px",
        }}
      >
        <button
          onClick={() => toggleCalendarMode("dueDate")}
          style={{
            flex: 1,
            padding: "6px",
            border: "none",
            borderRadius: "4px",
            backgroundColor:
              calendarMode === "dueDate" ? "#1976d2" : "transparent",
            color: calendarMode === "dueDate" ? "white" : "#666",
            cursor: updating ? "not-allowed" : "pointer",
            fontSize: "12px",
            fontWeight: 500,
            transition: "all 0.2s ease",
          }}
          disabled={updating}
        >
          Due Date
        </button>
        <button
          onClick={() => toggleCalendarMode("duration")}
          style={{
            flex: 1,
            padding: "6px",
            border: "none",
            borderRadius: "4px",
            backgroundColor:
              calendarMode === "duration" ? "#1976d2" : "transparent",
            color: calendarMode === "duration" ? "white" : "#666",
            cursor: updating ? "not-allowed" : "pointer",
            fontSize: "12px",
            fontWeight: 500,
            transition: "all 0.2s ease",
          }}
          disabled={updating}
        >
          Duration
        </button>
      </div>

      {/* Selection Info */}
      <div
        style={{
          marginBottom: "12px",
          padding: "8px",
          backgroundColor: "#f8f9fa",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        {calendarMode === "dueDate" ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="fas fa-calendar-day" style={{ color: "#1976d2" }}></i>
            <span>
              {selectedDueDate
                ? `Due Date: ${new Date(selectedDueDate).toLocaleDateString()}`
                : "No due date set"}
            </span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i
                className="fas fa-play"
                style={{ color: "#28a745", fontSize: "10px" }}
              ></i>
              <span>
                Start:{" "}
                {tempStartDate
                  ? tempStartDate.toLocaleDateString()
                  : selectedStartDate
                    ? new Date(selectedStartDate).toLocaleDateString()
                    : "Not selected"}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i
                className="fas fa-stop"
                style={{ color: "#dc3545", fontSize: "10px" }}
              ></i>
              <span>
                End:{" "}
                {tempEndDate
                  ? tempEndDate.toLocaleDateString()
                  : selectedEndDate
                    ? new Date(selectedEndDate).toLocaleDateString()
                    : "Not selected"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Calendar Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <button
          onClick={goToPreviousMonth}
          disabled={updating}
          style={{
            background: "#f0f0f0",
            border: "1px solid #ddd",
            borderRadius: "4px",
            padding: "4px 8px",
            cursor: updating ? "not-allowed" : "pointer",
            opacity: updating ? 0.5 : 1,
          }}
        >
          <i className="fas fa-chevron-left"></i>
        </button>
        <span style={{ fontWeight: "bold" }}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button
          onClick={goToNextMonth}
          disabled={updating}
          style={{
            background: "#f0f0f0",
            border: "1px solid #ddd",
            borderRadius: "4px",
            padding: "4px 8px",
            cursor: updating ? "not-allowed" : "pointer",
            opacity: updating ? 0.5 : 1,
          }}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>

      {/* Day Labels */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "2px",
          marginBottom: "5px",
        }}
      >
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <div
            key={day}
            style={{
              textAlign: "center",
              fontSize: "11px",
              fontWeight: "bold",
              color: "#666",
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "2px",
          marginBottom: "12px",
        }}
      >
        {generateCalendarDays()}
      </div>

      {/* Footer with Save Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          borderTop: "1px solid #eee",
          paddingTop: "12px",
          marginTop: "4px",
        }}
      >
        <button
          onClick={handleSave}
          disabled={
            updating ||
            (calendarMode === "duration" && (!tempStartDate || !tempEndDate))
          }
          style={{
            padding: "6px 16px",
            backgroundColor: "#1976d2",
            color: "white",
            border: "none",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: 500,
            cursor:
              updating ||
              (calendarMode === "duration" && (!tempStartDate || !tempEndDate))
                ? "not-allowed"
                : "pointer",
            opacity:
              updating ||
              (calendarMode === "duration" && (!tempStartDate || !tempEndDate))
                ? 0.5
                : 1,
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            if (
              !updating &&
              !(calendarMode === "duration" && (!tempStartDate || !tempEndDate))
            ) {
              e.currentTarget.style.backgroundColor = "#1557b0";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#1976d2";
          }}
        >
          Save
        </button>
      </div>

      <style jsx>{`
        .calendar-day {
          text-align: center;
          padding: 6px 2px;
          font-size: 12px;
          cursor: pointer;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        .calendar-day:hover:not(.disabled) {
          background-color: #e3f2fd !important;
        }
        .calendar-day.selected {
          background-color: #1976d2 !important;
          color: white !important;
        }
        .calendar-day.today {
          border: 1px solid #1976d2;
        }
        .calendar-day.disabled {
          color: #ccc;
          cursor: not-allowed;
        }
        .calendar-day.other-month {
          color: #999;
        }
        .calendar-day.in-range {
          background-color: #e3f2fd;
        }
        .calendar-day.range-start,
        .calendar-day.range-end {
          background-color: #1976d2 !important;
          color: white !important;
        }
      `}</style>
    </div>
  );
};

// Assignee Popup Component - Updated with minus icon and confirmation
const AssigneePopup = ({
  position,
  users,
  loading,
  error,
  selectedAssignees,
  onTaskAssigneeSelect,
  getInitials,
  getRandomColor,
  updating,
}) => {
  const [userToRemove, setUserToRemove] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleUserClick = (user, isSelected) => {
    if (updating) return;

    if (isSelected) {
      // Show confirmation dialog for removal
      setUserToRemove(user);
      setShowConfirmDialog(true);
    } else {
      // Directly add the user
      onTaskAssigneeSelect(user);
    }
  };

  const handleConfirmRemove = () => {
    if (userToRemove) {
      onTaskAssigneeSelect(userToRemove);
      setShowConfirmDialog(false);
      setUserToRemove(null);
    }
  };

  const handleCancelRemove = () => {
    setShowConfirmDialog(false);
    setUserToRemove(null);
  };

  return (
    <div
      className="user-popup-root"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 999999,
        backgroundColor: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
        width: "240px",
        maxHeight: "350px",
        display: "flex",
        flexDirection: "column",
        opacity: 1,
        color: "#333333",
        pointerEvents: updating ? "none" : "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {updating && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            zIndex: 2,
          }}
        >
          <i
            className="fas fa-spinner fa-spin"
            style={{ color: "#1976d2", fontSize: "24px" }}
          ></i>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "16px",
              width: "200px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: 600,
                marginBottom: "12px",
                color: "#333",
              }}
            >
              Remove Assignee?
            </div>
            <div
              style={{
                fontSize: "13px",
                marginBottom: "16px",
                color: "#666",
              }}
            >
              Are you sure you want to remove{" "}
              <strong>{userToRemove?.name}</strong> from this task?
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleCancelRemove}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #ddd",
                  backgroundColor: "white",
                  borderRadius: "4px",
                  fontSize: "12px",
                  cursor: "pointer",
                  color: "#666",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemove}
                style={{
                  padding: "6px 12px",
                  border: "none",
                  backgroundColor: "#dc3545",
                  color: "white",
                  borderRadius: "4px",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#c82333";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc3545";
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          padding: "12px",
          borderBottom: "1px solid #eee",
          fontWeight: "bold",
          flexShrink: 0,
          backgroundColor: "#fff",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>Select Assignees</span>
        <span
          style={{
            fontSize: "11px",
            color: "#666",
            backgroundColor: "#f0f0f0",
            padding: "2px 6px",
            borderRadius: "12px",
          }}
        >
          {selectedAssignees.length} assigned
        </span>
      </div>

      <div
        style={{
          maxHeight: "200px",
          overflowY: "auto",
          overflowX: "hidden",
          flex: 1,
        }}
      >
        {loading ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
            <i
              className="fas fa-spinner fa-spin"
              style={{ marginRight: "8px" }}
            ></i>
            Loading users...
          </div>
        ) : error ? (
          <div
            style={{ padding: "20px", textAlign: "center", color: "#dc3545" }}
          >
            <i
              className="fas fa-exclamation-circle"
              style={{ marginRight: "8px" }}
            ></i>
            {error}
          </div>
        ) : users && users.length > 0 ? (
          users.map((user) => {
            const isSelected = selectedAssignees.some((a) => a.id === user.id);

            return (
              <div
                key={user.id}
                onClick={() => handleUserClick(user, isSelected)}
                style={{
                  padding: "10px 12px",
                  cursor: updating ? "not-allowed" : "pointer",
                  backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                  borderBottom: "1px solid #f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  opacity: updating ? 0.5 : 1,
                  transition: "background-color 0.2s ease",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  if (!updating) {
                    e.currentTarget.style.backgroundColor = isSelected
                      ? "#e3f2fd"
                      : "#f5f5f5";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!updating) {
                    e.currentTarget.style.backgroundColor = isSelected
                      ? "#e3f2fd"
                      : "transparent";
                  }
                }}
              >
                <div
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    backgroundColor: user.color || getRandomColor(user.id),
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "12px",
                    flexShrink: 0,
                  }}
                >
                  {getInitials(user.name)}
                </div>
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {user.name}
                </span>
                {isSelected ? (
                  <div
                    style={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                    }}
                  >
                    <i
                      className="fas fa-check"
                      style={{ color: "#1976d2", flexShrink: 0 }}
                    />
                    <i
                      className="fas fa-minus-circle"
                      style={{
                        color: "#dc3545",
                        flexShrink: 0,
                        fontSize: "14px",
                      }}
                    />
                  </div>
                ) : (
                  <i
                    className="fas fa-plus-circle"
                    style={{ color: "#28a745", flexShrink: 0 }}
                    title="Click to assign"
                  />
                )}
              </div>
            );
          })
        ) : (
          <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
            <i
              className="fas fa-users"
              style={{ marginRight: "8px", fontSize: "24px", opacity: 0.5 }}
            ></i>
            <div style={{ marginTop: "8px" }}>No users available</div>
          </div>
        )}
      </div>

      {/* Selected users summary */}
      {selectedAssignees.length > 0 && (
        <div
          style={{
            padding: "8px 12px",
            borderTop: "1px solid #eee",
            backgroundColor: "#f8f9fa",
            fontSize: "11px",
            color: "#666",
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }}
        ></div>
      )}
    </div>
  );
};

// Status Popup Component
const StatusPopup = ({
  position,
  taskStatuses,
  selectedStatus,
  onTaskStatusSelect,
  updating,
}) => {
  const defaultStatuses = [
    {
      id: 1,
      name: "To Do",
      icon: "fa-circle",
      color: "#6c757d",
      bgColor: "#e9ecef",
    },
    {
      id: 2,
      name: "In Progress",
      icon: "fa-spinner",
      color: "#fd7e14",
      bgColor: "#fff3e0",
    },
    {
      id: 3,
      name: "Archived",
      icon: "fa-archive",
      color: "#6c757d",
      bgColor: "#f8d7da",
    },
    {
      id: 4,
      name: "Blocked",
      icon: "fa-times",
      color: "#dc3545",
      bgColor: "#f8d7da",
    },
    {
      id: 5,
      name: "Done",
      icon: "fa-check-circle",
      color: "#28a745",
      bgColor: "#d4edda",
    },
  ];

  const statuses =
    taskStatuses && taskStatuses.length > 0 ? taskStatuses : defaultStatuses;

  return (
    <div
      className="status-popup-root"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 999999,
        backgroundColor: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
        width: "200px",
        opacity: 1,
        color: "#333333",
        pointerEvents: updating ? "none" : "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {updating && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            zIndex: 1,
          }}
        >
          <i
            className="fas fa-spinner fa-spin"
            style={{ color: "#1976d2", fontSize: "24px" }}
          ></i>
        </div>
      )}
      <div
        style={{
          padding: "12px",
          borderBottom: "1px solid #eee",
          fontWeight: "bold",
        }}
      >
        Select Status
      </div>

      <div>
        {statuses.map((status) => (
          <div
            key={status.id}
            onClick={() => !updating && onTaskStatusSelect(status)}
            style={{
              padding: "10px 12px",
              cursor: updating ? "not-allowed" : "pointer",
              backgroundColor:
                selectedStatus?.id === status.id ? "#f8f9fa" : "transparent",
              borderBottom: "1px solid #f5f5f5",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              borderLeft:
                selectedStatus?.id === status.id
                  ? `3px solid ${status.color}`
                  : "3px solid transparent",
              opacity: updating ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!updating) {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
              }
            }}
            onMouseLeave={(e) => {
              if (!updating) {
                e.currentTarget.style.backgroundColor =
                  selectedStatus?.id === status.id ? "#f8f9fa" : "transparent";
              }
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "4px",
                backgroundColor: status.bgColor,
                color: status.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                flexShrink: 0,
              }}
            >
              <i className={`fas ${status.icon}`}></i>
            </div>
            <span style={{ flex: 1 }}>{status.name}</span>
            {selectedStatus?.id === status.id && (
              <i
                className="fas fa-check"
                style={{
                  marginLeft: "auto",
                  color: status.color,
                  flexShrink: 0,
                }}
              ></i>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ task, onConfirm, onCancel, deleting, errorMessage  }) => {
  // Handle escape key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape" && !deleting) {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [onCancel, deleting]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div
      className="delete-confirm-modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999999,
        //backdropFilter: "blur(3px)",
        margin: 0,
        padding: 0,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !deleting) {
          onCancel();
        }
      }}
    >
      <div
        className="delete-confirm-modal-content"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          width: "380px",
          maxWidth: "90%",
          padding: "28px",
          position: "relative",
          animation: "modalFadeIn 0.2s ease-out",
          margin: "0 auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {deleting && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "12px",
              zIndex: 1,
            }}
          >
            <i
              className="fas fa-spinner fa-spin"
              style={{ color: "#dc3545", fontSize: "36px" }}
            ></i>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              backgroundColor: "#fee",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="fas fa-exclamation-triangle"
              style={{
                color: "#dc3545",
                fontSize: "36px",
              }}
            ></i>
          </div>
        </div>

        <h3
          style={{
            margin: "0 0 12px 0",
            fontSize: "20px",
            fontWeight: 600,
            color: "#333",
            textAlign: "center",
          }}
        >
          Delete Task
        </h3>

        <p
          style={{
            margin: "0 0 20px 0",
            fontSize: "15px",
            color: "#666",
            textAlign: "center",
            lineHeight: "1.6",
          }}
        >
          Are you sure you want to delete <strong>"{task.title}"</strong>?
        </p>

        <p
          style={{
            margin: "0 0 20px 0",
            fontSize: "13px",
            color: "#dc3545",
            textAlign: "center",
            lineHeight: "1.5",
            backgroundColor: "#fff5f5",
            padding: "8px 12px",
            borderRadius: "6px",
            border: "1px solid #ffebee",
          }}
        >
          <i
            className="fas fa-exclamation-circle"
            style={{ marginRight: "6px" }}
          ></i>
          This action cannot be undone.
        </p>
          {errorMessage && (
            <div
              style={{
                margin: "0 0 20px 0",
                padding: "12px",
                backgroundColor: "#fff5f5",
                border: "1px solid #dc3545",
                borderRadius: "8px",
                color: "#dc3545",
                fontSize: "13px",
                textAlign: "center",
                animation: "shake 0.3s ease-in-out",
              }}
            >
              <i className="fas fa-exclamation-circle" style={{ marginRight: "8px" }}></i>
              {errorMessage}
            </div>
          )}

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
          }}
        >
          <button
            onClick={onCancel}
            disabled={deleting}
            style={{
              padding: "12px 24px",
              border: "1px solid #ddd",
              backgroundColor: "white",
              color: "#666",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 500,
              cursor: deleting ? "not-allowed" : "pointer",
              opacity: deleting ? 0.5 : 1,
              flex: 1,
              transition: "all 0.2s ease",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
            onMouseEnter={(e) => {
              if (!deleting) {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
              }
            }}
            onMouseLeave={(e) => {
              if (!deleting) {
                e.currentTarget.style.backgroundColor = "white";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)";
              }
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            style={{
              padding: "12px 24px",
              border: "none",
              backgroundColor: "#dc3545",
              color: "white",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 500,
              cursor: deleting ? "not-allowed" : "pointer",
              opacity: deleting ? 0.5 : 1,
              flex: 1,
              transition: "all 0.2s ease",
              boxShadow: "0 2px 4px rgba(220,53,69,0.3)",
            }}
            onMouseEnter={(e) => {
              if (!deleting) {
                e.currentTarget.style.backgroundColor = "#c82333";
                e.currentTarget.style.boxShadow =
                  "0 4px 8px rgba(220,53,69,0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (!deleting) {
                e.currentTarget.style.backgroundColor = "#dc3545";
                e.currentTarget.style.boxShadow =
                  "0 2px 4px rgba(220,53,69,0.3)";
              }
            }}
          >
            {deleting ? (
              <>
                <i
                  className="fas fa-spinner fa-spin"
                  style={{ marginRight: "8px" }}
                ></i>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes modalFadeIn {
            from {
              opacity: 0;
              transform: scale(0.9) translateY(10px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

TaskCard.propTypes = {
  task: PropTypes.object.isRequired,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  onNotesToggle: PropTypes.func,
  hasNotes: PropTypes.number,
  children: PropTypes.node,
  // Task-specific props
  onTaskDueDateSelect: PropTypes.func,
  onTaskAssigneeSelect: PropTypes.func,
  onTaskStatusSelect: PropTypes.func,
  onAddNote: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refreshNotes: PropTypes.func,
  job: PropTypes.object,
  fetchTasksForJob: PropTypes.func,
};

TaskCard.defaultProps = {
  isExpanded: false,
  onToggle: () => {},
  onNotesToggle: () => {},
  hasNotes: 0,
  onTaskDueDateSelect: () => {},
  onTaskAssigneeSelect: () => {},
  onTaskStatusSelect: () => {},
  onAddNote: () => {},
  onEdit: () => {},
  onDelete: () => {},
  userId: null,
  refreshNotes: () => {},
  job: null,
  fetchTasksForJob: () => {},
};

DeleteConfirmationModal.propTypes = {
  task: PropTypes.object.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  deleting: PropTypes.bool,
};

DeleteConfirmationModal.defaultProps = {
  deleting: false,
};

export default TaskCard;
